import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { Pagination, SortChangedEvent, Table } from "@performyard/py-components";

// components
import Button from "../../../../../components/Button";
import ReviewTemplateModal from "../../../../../components/ReviewTemplateModal";
import { SideNavigationLink } from "../../../../../components/SideNavigation";
import AllReviewsFilters, {
	AllReviewsFilters as AllReviewsFiltersType,
	StatusSelectionOptions,
} from "./components/AllReviewsFilters";
import AllReviewsReadableFiltersList from "./components/AllReviewsReadableFiltersList";
import getAllReviewsTableCols from "./components/AllReviewsTableCols";

// hooks
import { useDirectReports, useSearchService, useSearchServicePrefetch } from "../../../../../api/hooks";

//utils
import { SERVICE_REVIEW } from "../../../../../api/services/defs/search-service";
import { Review } from "../../../../../api/services/searchServiceResultTypes/reviews";

import { getUserID } from "../../../../../selectors/app";

import { getFiltersForSearchService } from "./utils";

//templates
import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
//constants
import { CompanyReviewsNavigationLinks, REVIEWS_TABLE_LIMIT } from "../../constants";

type AllReviewsProps = {
	navLinks: SideNavigationLink[];
};

const AllReviews = ({ navLinks }: AllReviewsProps) => {
	const navigate = useNavigate();
	const { page } = useParams<{ page: string }>();
	const userID = useSelector((state) => getUserID(state));
	const { data: directReportData } = useDirectReports(userID);

	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);

	const [isNewReviewModalOpen, setIsNewReviewModalOpen] = useState<boolean>(false);
	const [activeReviewTemplate, setActiveReviewTemplate] = useState<string | null>(null);

	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		filters,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState<AllReviewsFiltersType>({
		defaultSortName: "start_date",
		defaultSortDirection: "desc",
		defaultPageNumber: 1,
		fallbackFilters: {
			status: [StatusSelectionOptions[0].value, StatusSelectionOptions[2].value],
		},
	});

	const handleTemplateInputChange = (value: string | null) => {
		setActiveReviewTemplate(value);
	};

	const createReviewURL = activeReviewTemplate
		? `/start-review/${activeReviewTemplate}`
		: "/start-review/?subject=null";

	const { data, isLoading } = useSearchService<Review>({
		service: SERVICE_REVIEW,
		filters: getFiltersForSearchService(filters, debouncedSearchValue, userID, directReportData),
		skip: (pageNumber - 1) * REVIEWS_TABLE_LIMIT,
		limit: REVIEWS_TABLE_LIMIT,
		sort,
		defaultErrorMessage: "There was an error getting Reviews",
	});

	//prefetch the review templates 1 page before and 3 after

	useSearchServicePrefetch<Review>(
		{
			service: SERVICE_REVIEW,
			filters: getFiltersForSearchService(filters, debouncedSearchValue, userID, directReportData),
			skip: (pageNumber - 2) * REVIEWS_TABLE_LIMIT,
			limit: REVIEWS_TABLE_LIMIT,
			sort,
		},
		pageNumber <= 1 ? 0 : 1
	);

	useSearchServicePrefetch<Review>(
		{
			service: SERVICE_REVIEW,
			filters: getFiltersForSearchService(filters, debouncedSearchValue, userID, directReportData),
			skip: pageNumber * REVIEWS_TABLE_LIMIT,
			limit: REVIEWS_TABLE_LIMIT,
			sort,
		},
		3
	);

	const reviews = data?.results || [];
	const totalReviews = data?.total || 0;

	const columnDefs = useMemo(() => getAllReviewsTableCols(), []);

	const onSortChanged = (event: SortChangedEvent<Review>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn?.colId) {
			setSort({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={(value) => setDebouncedSearch(value)}
			onFilterButtonPress={() => {
				setIsFiltersPanelOpen(true);
			}}
			activeFiltersCount={Object.keys(filters).length}
			searchPlaceholder="Search reviews..."
		/>
	);

	const toolbarInformationRow = <AllReviewsReadableFiltersList filters={filters} setFilters={setFilters} />;

	const actionButtons = [
		<Button color="green" key="create-review-cycle-button" href="/review-cycle/create/">
			New Review Cycle
		</Button>,
		<Button
			color="green"
			testid="new-review-button"
			onClick={() => setIsNewReviewModalOpen(true)}
			key="create-review-button"
		>
			New Review
		</Button>,
	];

	const drawer = (
		<AllReviewsFilters
			filters={filters}
			isOpen={isFiltersPanelOpen}
			onClose={() => setIsFiltersPanelOpen(false)}
			onApplyFilters={(newFilters) => setFilters(newFilters)}
		/>
	);

	const mainContent = (
		<div>
			<Table<Review>
				autoHeight
				rowData={reviews}
				columnDefs={columnDefs}
				loading={isLoading}
				getRowId={(params) => params.data._id}
				onSortChanged={onSortChanged}
				emptyStateText="No Reviews Found"
				initialState={{
					sort: {
						sortModel: sort.name ? [{ colId: sort.name, sort: sort.direction as "asc" | "desc" }] : [],
					},
				}}
				animateRows={false}
			/>
			{totalReviews > 0 && (
				<div className="mx-auto mt-5 flex w-full max-w-[400px] justify-center">
					<Pagination
						currentPage={pageNumber}
						pageCount={Math.ceil(totalReviews / REVIEWS_TABLE_LIMIT)}
						onPageChange={(page: number) => setPageNumber(page)}
					/>
				</div>
			)}
		</div>
	);

	return (
		<div>
			<InfoPageTemplate
				toolbarActionRow={toolbarActionRow}
				toolbarInformationRow={toolbarInformationRow}
				actionButtons={actionButtons}
				mainContent={mainContent}
				drawer={drawer}
				navigationLinks={navLinks || CompanyReviewsNavigationLinks}
				activeNavigationLinkKey={page}
			/>
			{isNewReviewModalOpen ? (
				<ReviewTemplateModal
					handleTemplateInputChange={handleTemplateInputChange}
					handleModalCancel={() => setIsNewReviewModalOpen(false)}
					createReview={() => {
						navigate(createReviewURL);
					}}
				/>
			) : null}
		</div>
	);
};

export default AllReviews;
